<template>
  <section
    ref="$block"
    class="osk-home-blocks-blog"
  >
    <div class="osk-wrapper">
      <h2
        v-if="data.title"
        class="osk-home-blocks-blog__title"
      >
        {{ data.title }}
      </h2>

      <UikitSwiper
        ref="$swiper"
        navigation
        pagination
        :breakpoints="sliderBreakpoints"
        :css-mode="!isDesktop"
        off
        class="osk-home-blocks-blog__swiper"
        @slide-change="handleSlideChange"
      >
        <UikitSwiperSlide
          v-for="(item, index) in items"
          :key="index"
        >
          <a
            class="osk-home-blocks-blog__card"
            :href="item.link"
            target="blank"
            @click="handleClick(item, index)"
          >
            <img
              :src="item.imgPath"
              alt="main-image"
              class="osk-home-blocks-blog__card-image"
            >

            <div class="osk-home-blocks-blog__card-title">
              {{ item.title }}
            </div>
          </a>
        </UikitSwiperSlide>
      </UikitSwiper>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { PrimaryContentDTO, BlogDTO } from '~/restAPI/data-contracts';

const props = defineProps<{
  data: PrimaryContentDTO
}>();

const $block = ref<ComponentPublicInstance>();
const $swiper = ref();

const sliderBreakpoints = {
  0: {
    slidesPerView: 1,
    slidesPerGroup: 1,
    spaceBetween: 12,
    navigation: { enabled: false },
    pagination: { enabled: true },
  },
  767: {
    slidesPerView: 2,
    slidesPerGroup: 2,
    spaceBetween: 16,
    navigation: { enabled: true },
    pagination: { enabled: false },
  },
  1024: {
    slidesPerView: 2,
    slidesPerGroup: 2,
    spaceBetween: 24,
    navigation: { enabled: true },
    pagination: { enabled: false },
  },
};

const { isDesktop } = useUiBreakpoints();

const items = computed(() => (props.data.contentList ?? []) as BlogDTO[]);

const allIds = computed(() => items.value.map((i) => i.id!));

const { event, clickEvent } = useSwiperAnalytics(
  $swiper,
  $block,
  allIds,
  {
    chapter: props.data.contentType,
    chapter_index: props.data.chapterIndex,
    segment_id: props.data.segmentId,
    id_chapter: props.data.id,
  },
  sliderBreakpoints,
);

async function handleClick(item: BlogDTO, index: number) {
  clickEvent({
    id_item: item.id,
    index,
    item: 'blog',
  });
}

function handleSlideChange() {
  event();
}
</script>

<style lang="scss">
@import "~/assets/scss/settings/index";

.osk-home-blocks-blog {
  padding-bottom: 42px;

  @include media-query(lg-and-up) {
    padding-bottom: 82px;
  }

  .osk-wrapper {
    padding: 0;

    @include media-query(md-and-up) {
      padding: 0 16px;
    }
  }

  &__title {
    @include font-heading;
    padding: 0 16px 10px;

    @include media-query(lg-and-up) {
      text-transform: uppercase;
      text-align: center;
      padding-bottom: 34px;
    }
  }

  &__card {
    display: flex;
    flex-direction: column;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      background: url(~/assets/img/home/gradient.png) repeat bottom;
      background-size: contain;

      @include media-query(md-and-up) {
        display: none;
      }
    }
  }

  &__card-image {
    display: block;
    width: 100%;
    height: 390px;
    object-fit: cover;
    border-radius: 2px;

    @include media-query(md-and-up) {
      margin-bottom: 14px;
      border-radius: 4px;
      height: 326px;
    }
  }

  &__card-title {
    @include line-clamp;
    @include font-style($font-size-heading, $line-height-heading, $font-weight-medium);
    position: absolute;
    bottom: 40px;
    padding: 0 16px;
    color: #fff;
    z-index: 1;
    overflow: hidden;

    @include media-query(md-and-up) {
      @include font-style($font-size-subheading, $line-height-subheading, $font-weight-medium);
      color: #000;
      position: static;
      padding: 0;
      padding-right: 16px;
    }
  }

  @include swiper-section-nav;

  .swiper-pagination {
    display: flex;
    justify-content: center;
    bottom: 16px;
  }

  .swiper-pagination-bullet {
    background: #fff;
    opacity: 0.4;
    width: 6px;
    height: 6px;
    margin: 0 4px !important;
  }

  .swiper-pagination-bullet-active {
    opacity: 1;
  }

  .osk-swiper_off {
    .swiper-wrapper {
      overflow: visible;
      justify-content: space-between;
    }

    .swiper-slide {
      max-width: 100%;

      @include media-query(md-and-up) {
        max-width: 50%;
        margin-right: 24px;
        flex-shrink: 0;
      }
    }
  }
}
</style>
